import React, { useState, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Input, Table, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { COLUMNAS_MASTERS, COLUMNAS_VARIANTES, leerHoja, hojaPorNombre, armarPlantillaProductos } from "./plantillaProductos";

function CargaMasivaArticulos() {
  const { user } = useContext(AuthContext);
  const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;
  const URL_LINEAS = process.env.REACT_APP_URL_LINEAS;
  const URL_MARCAS = process.env.REACT_APP_URL_MARCAS;
  const URL_PROVEEDORES = process.env.REACT_APP_URL_PROVEEDORES;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const [modal, setModal] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [carga, setCarga] = useState(null); // { payload, resultado, archivo }

  const toggle = () => {
    if (trabajando) return;
    setModal(!modal);
    setCarga(null);
  };

  const descargarPlantilla = async () => {
    setTrabajando(true);
    try {
      const [lin, mar, prov, arts] = await Promise.all([
        axios.get(URL_LINEAS, { headers }),
        axios.get(URL_MARCAS, { headers }),
        axios.get(URL_PROVEEDORES, { headers }),
        axios.get(URL_ARTICULOS, { headers }),
      ]);
      const activos = (l, campo) =>
        (l.data || [])
          .filter((x) => x.is_active !== "No")
          .map((x) => x[campo])
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
      const lineas = activos(lin, "name");
      const marcas = activos(mar, "name");
      const proveedores = activos(prov, "nombre_comercial");

      // Masters que ya existen: van en la hoja MASTERS para copiar su SKU en VARIANTES
      const nombre = (l, campo) => (Array.isArray(l) && l[0] && l[0][campo]) || "";
      const masters = (arts.data || [])
        .filter((a) => a.modelo === "MASTER" && a.is_active !== "No")
        .sort((a, b) => String(a.SKU).localeCompare(String(b.SKU)))
        .map((a) => ({
          sku: a.SKU,
          nombre: a.nombre,
          linea: nombre(a.lineas, "name"),
          marca: nombre(a.marcas, "name"),
          proveedor: nombre(a.proveedores, "nombre_comercial"),
          medida: a.medida && a.medida !== "NA" ? a.medida : "",
          mlm: a.MLM && a.MLM !== "NA" ? a.MLM : "",
        }));

      const wb = armarPlantillaProductos({ lineas, marcas, proveedores, masters });
      XLSX.writeFile(wb, "Plantilla Productos.xlsx");
    } catch (error) {
      console.log(error);
      Swal.fire({ icon: "error", title: "No se pudo generar la plantilla", text: error.message });
    }
    setTrabajando(false);
  };

  const enviar = (payload, ejecutar) =>
    axios
      .post(`${URL_ARTICULOS}CargaMasiva`, { ...payload, ejecutar }, { headers })
      .then((r) => r.data)
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.errores) return error.response.data;
        throw error;
      });

  const subirArchivo = (event) => {
    const archivo = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!archivo) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const wsM = hojaPorNombre(wb, "masters");
        const wsV = hojaPorNombre(wb, "variantes");
        if (!wsM && !wsV) {
          Swal.fire({ icon: "error", title: "Archivo no válido", text: "El Excel debe tener la hoja MASTERS y/o la hoja VARIANTES de la plantilla." });
          return;
        }
        const m = wsM ? leerHoja(wsM, COLUMNAS_MASTERS) : { filas: [], faltan: [] };
        const v = wsV ? leerHoja(wsV, COLUMNAS_VARIANTES) : { filas: [], faltan: [] };
        const faltan = [
          ...(m.filas.length ? m.faltan.map((c) => `MASTERS: ${c}`) : []),
          ...(v.filas.length ? v.faltan.map((c) => `VARIANTES: ${c}`) : []),
        ];
        if (faltan.length) {
          Swal.fire({ icon: "error", title: "Faltan columnas", html: faltan.join("<br/>") });
          return;
        }
        if (!m.filas.length && !v.filas.length) {
          Swal.fire({ icon: "warning", title: "Sin renglones", text: "Las hojas MASTERS y VARIANTES están vacías." });
          return;
        }
        setTrabajando(true);
        const payload = { masters: m.filas, variantes: v.filas };
        const resultado = await enviar(payload, false);
        setCarga({ payload, resultado, archivo: archivo.name });
      } catch (error) {
        console.log(error);
        Swal.fire({ icon: "error", title: "No se pudo revisar el archivo", text: (error.response && error.response.data && error.response.data.message) || error.message });
      }
      setTrabajando(false);
    };
    reader.readAsArrayBuffer(archivo);
  };

  const crear = async () => {
    const { resumen } = carga.resultado;
    const ok = await Swal.fire({
      title: "¿Crear los productos?",
      text: `Se crearán ${resumen.mastersNuevos} masters y ${resumen.variantesNuevas} variantes${resumen.variantesUnicas ? ` (más ${resumen.variantesUnicas} variante(s) única(s) para los masters sin variantes)` : ""}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, crear",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setTrabajando(true);
    try {
      const r = await enviar(carga.payload, true);
      if (!r.ok) {
        setCarga({ ...carga, resultado: r });
        Swal.fire({ icon: "error", title: "No se completó la carga", text: r.errores.map((e) => e.mensaje).join(" · ") });
        setTrabajando(false);
        return;
      }
      await axios
        .post(
          URL_LOGS,
          { tipo: "Carga masiva de productos", detalle: `${carga.archivo}: ${r.creados.masters} masters / ${r.creados.variantes} variantes`, user: user.id },
          { headers }
        )
        .catch(() => {});
      await Swal.fire("Listo", `Se crearon ${r.creados.masters} masters y ${r.creados.variantes} variantes${r.creados.variantesUnicas ? ` (${r.creados.variantesUnicas} única(s) de masters sin variantes)` : ""}.`, "success");
      window.location.reload();
    } catch (error) {
      console.log(error);
      Swal.fire({ icon: "error", title: "Oops...", text: (error.response && error.response.data && error.response.data.message) || error.message });
    }
    setTrabajando(false);
  };

  const r = carga && carga.resultado;
  const tablaScroll = { maxHeight: 260, overflowY: "auto", marginBottom: 12 };
  const chip = (texto, valor, color) => (
    <span style={{ display: "inline-block", background: color, color: "#fff", borderRadius: 6, padding: "4px 10px", marginRight: 8, fontSize: "0.85rem" }}>
      {texto}: <b>{valor}</b>
    </span>
  );

  return (
    <>
      <Button size="sm" color="info" onClick={toggle} disabled={!user.articulos_create}>
        <i className="fas fa-file-upload"></i> Carga masiva
      </Button>

      <Modal size="xl" isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Carga masiva de productos (masters y variantes)</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={5}>
              <p style={{ fontSize: "0.85rem", color: "#475569" }}>
                1. Descarga la plantilla, llena las hojas <b>MASTERS</b> y <b>VARIANTES</b> (las instrucciones vienen en el archivo).
              </p>
              <Button color="success" size="sm" onClick={descargarPlantilla} disabled={trabajando}>
                <i className="fas fa-file-excel"></i> Descargar plantilla
              </Button>
            </Col>
            <Col md={7}>
              <p style={{ fontSize: "0.85rem", color: "#475569" }}>2. Sube el Excel lleno. Primero se revisa; nada se crea hasta que confirmes.</p>
              <Input type="file" accept=".xlsx,.xls" onChange={subirArchivo} disabled={trabajando} />
            </Col>
          </Row>

          {trabajando && (
            <div style={{ textAlign: "center", margin: 20 }}>
              <Spinner style={{ color: "#232B41" }} />
            </div>
          )}

          {r && !trabajando && (
            <div style={{ marginTop: 18 }}>
              <h5>Revisión de {carga.archivo}</h5>
              <div style={{ marginBottom: 12 }}>
                {chip("Masters nuevos", r.resumen.mastersNuevos || 0, "#8b5cf6")}
                {chip("Variantes nuevas", r.resumen.variantesNuevas || 0, "#0ea5e9")}
                {r.resumen.variantesUnicas > 0 && chip("Variante única (masters sin variantes)", r.resumen.variantesUnicas, "#ec4899")}
                {chip("Ya existían (no se tocan)", r.resumen.yaExistian || 0, "#64748b")}
                {chip("Errores", r.errores.length, r.errores.length ? "#dc2626" : "#16a34a")}
              </div>

              {r.errores.length > 0 && (
                <>
                  <p style={{ color: "#dc2626", fontWeight: 600 }}>
                    Hay errores: no se creará nada. Corrige el Excel y vuelve a subirlo.
                  </p>
                  <div style={tablaScroll}>
                    <Table size="sm" striped>
                      <thead>
                        <tr><th>Hoja</th><th>Renglón</th><th>SKU</th><th>Problema</th></tr>
                      </thead>
                      <tbody>
                        {r.errores.map((e, i) => (
                          <tr key={i}><td>{e.hoja}</td><td>{e.renglon}</td><td>{e.sku}</td><td>{e.mensaje}</td></tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              {r.errores.length === 0 && r.vistaPrevia && (
                <>
                  {r.vistaPrevia.masters.length > 0 && (
                    <div style={tablaScroll}>
                      <Table size="sm" striped>
                        <thead>
                          <tr><th>Renglón</th><th>SKU MASTER</th><th>Nombre</th></tr>
                        </thead>
                        <tbody>
                          {r.vistaPrevia.masters.map((m, i) => (
                            <tr key={i}><td>{m.renglon}</td><td>{m.SKU}</td><td>{m.nombre}</td></tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                  {r.vistaPrevia.variantes.length > 0 && (
                    <div style={tablaScroll}>
                      <Table size="sm" striped>
                        <thead>
                          <tr><th>Renglón</th><th>SKU</th><th>Nombre</th><th>Variante</th><th>Factor</th><th>Costo MXN</th><th>Master</th></tr>
                        </thead>
                        <tbody>
                          {r.vistaPrevia.variantes.map((v, i) => (
                            <tr key={i}>
                              <td>{v.renglon}</td><td>{v.SKU}</td><td>{v.nombre}</td><td>{v.variante}</td>
                              <td>{v.factor}</td><td>{v.costoMX}</td><td>{v.master === "NA" ? "— sin master —" : v.master}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}

              {r.avisos.length > 0 && (
                <details>
                  <summary style={{ cursor: "pointer", color: "#64748b" }}>Avisos ({r.avisos.length})</summary>
                  <div style={tablaScroll}>
                    <Table size="sm">
                      <tbody>
                        {r.avisos.map((a, i) => (
                          <tr key={i}><td>{a.hoja}</td><td>{a.renglon}</td><td>{a.sku}</td><td>{a.mensaje}</td></tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </details>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {r && r.ok && !trabajando && (r.resumen.mastersNuevos > 0 || r.resumen.variantesNuevas > 0) && (
            <Button color="success" onClick={crear}>
              Crear {r.resumen.mastersNuevos} masters y {r.resumen.variantesNuevas} variantes
            </Button>
          )}
          <Button color="secondary" onClick={toggle} disabled={trabajando}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CargaMasivaArticulos;
